import React, { useState, useEffect } from "react";
import {CheckCircle, ArrowRight, Instagram, TrendingUp, Target, Users, Sparkles} from 'lucide-react';

function App() {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [showStickyCTA, setShowStickyCTA] = useState(false);
  const [visibleSections, setVisibleSections] = useState<Set<number>>(new Set());

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (scrollTop / docHeight) * 100;
      setScrollProgress(progress);
      setShowStickyCTA(scrollTop > 800);
    };

    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const index = parseInt(entry.target.getAttribute('data-reveal') || '0');
          setVisibleSections(prev => new Set([...prev, index]));
        }
      });
    }, observerOptions);

    document.querySelectorAll('[data-reveal]').forEach(el => observer.observe(el));
    window.addEventListener('scroll', handleScroll);

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-slate-950 to-black">
      {/* Progress Bar de Leitura */}
      <div className="fixed top-0 left-0 w-full h-1 bg-slate-900/50 z-50">
        <div 
          className="h-full bg-gradient-to-r from-blue-500 via-orange-500 to-orange-600 transition-all duration-300 shadow-lg shadow-orange-500/50"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>

      {/* Sticky CTA Flutuante */}
      <div className={`fixed bottom-6 right-6 z-40 transition-all duration-500 ${showStickyCTA ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0 pointer-events-none'}`}>
        <a
          href="https://pay.kiwify.com.br/conkdhF"
          target="_blank"
          rel="noopener noreferrer"
          className="group flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-600 to-orange-500 text-white font-bold rounded-full hover:shadow-2xl hover:shadow-orange-500/60 transition-all duration-300 hover:scale-110 animate-pulse"
        >
          <Sparkles className="w-5 h-5" />
          <span className="hidden sm:inline">Garantir Acesso</span>
          <span className="sm:hidden">Comprar</span>
        </a>
      </div>
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Animated Engagement Rain Background - Enhanced */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <style>{`
            @keyframes float-down {
              0% { transform: translateY(-100px) translateX(0) rotate(0deg); opacity: 0; }
              10% { opacity: 0.4; }
              50% { transform: translateY(50vh) translateX(20px) rotate(180deg); }
              90% { opacity: 0.4; }
              100% { transform: translateY(100vh) translateX(-20px) rotate(360deg); opacity: 0; }
            }
            @keyframes float-diagonal {
              0% { transform: translateY(-100px) translateX(-50px) rotate(0deg); opacity: 0; }
              10% { opacity: 0.5; }
              50% { transform: translateY(50vh) translateX(50px) rotate(180deg); }
              90% { opacity: 0.5; }
              100% { transform: translateY(100vh) translateX(150px) rotate(360deg); opacity: 0; }
            }
            @keyframes float-zigzag {
              0% { transform: translateY(-100px) translateX(0) rotate(0deg); opacity: 0; }
              10% { opacity: 0.3; }
              25% { transform: translateY(25vh) translateX(30px) rotate(90deg); }
              50% { transform: translateY(50vh) translateX(-30px) rotate(180deg); }
              75% { transform: translateY(75vh) translateX(30px) rotate(270deg); }
              90% { opacity: 0.3; }
              100% { transform: translateY(100vh) translateX(0) rotate(360deg); opacity: 0; }
            }
            .engagement-item {
              position: absolute;
              animation: float-down linear infinite;
              will-change: transform, opacity;
            }
            .engagement-glow {
              filter: drop-shadow(0 0 8px rgba(251, 146, 60, 0.6));
            }
            .engagement-blue-glow {
              filter: drop-shadow(0 0 8px rgba(59, 130, 246, 0.6));
            }
          `}</style>
          {[
          { icon: '❤️', delay: '-7s', duration: '14s', left: '8%', size: '28px', animation: 'float-down', glow: 'engagement-glow' },
          { icon: '👥', delay: '-12s', duration: '18s', left: '18%', size: '24px', animation: 'float-diagonal', glow: '' },
          { icon: '💬', delay: '-3s', duration: '16s', left: '28%', size: '26px', animation: 'float-zigzag', glow: '' },
          { icon: '🔄', delay: '-9s', duration: '17s', left: '38%', size: '22px', animation: 'float-down', glow: '' },
          { icon: '😍', delay: '-5s', duration: '19s', left: '48%', size: '30px', animation: 'float-diagonal', glow: 'engagement-glow' },
          { icon: '🔥', delay: '-11s', duration: '15s', left: '58%', size: '32px', animation: 'float-down', glow: 'engagement-glow' },
          { icon: '💯', delay: '-8s', duration: '18s', left: '68%', size: '24px', animation: 'float-zigzag', glow: '' },
          { icon: '⭐', delay: '-2s', duration: '16s', left: '78%', size: '26px', animation: 'float-diagonal', glow: 'engagement-blue-glow' },
          { icon: '📈', delay: '-13s', duration: '17s', left: '88%', size: '28px', animation: 'float-down', glow: 'engagement-blue-glow' },
          { icon: '💙', delay: '-6s', duration: '19s', left: '13%', size: '30px', animation: 'float-diagonal', glow: 'engagement-blue-glow' },
          { icon: '👍', delay: '-10s', duration: '16s', left: '23%', size: '24px', animation: 'float-zigzag', glow: '' },
          { icon: '✨', delay: '-4s', duration: '15s', left: '33%', size: '22px', animation: 'float-down', glow: 'engagement-glow' },
          { icon: '🎯', delay: '-14s', duration: '18s', left: '43%', size: '26px', animation: 'float-diagonal', glow: '' },
          { icon: '💫', delay: '-1s', duration: '17s', left: '53%', size: '28px', animation: 'float-zigzag', glow: 'engagement-blue-glow' },
          { icon: '🚀', delay: '-8.5s', duration: '16s', left: '63%', size: '30px', animation: 'float-down', glow: 'engagement-glow' },
          { icon: '💰', delay: '-5.5s', duration: '19s', left: '73%', size: '26px', animation: 'float-diagonal', glow: '' },
          { icon: '🎉', delay: '-12.5s', duration: '15s', left: '83%', size: '24px', animation: 'float-zigzag', glow: 'engagement-glow' },
          { icon: '📱', delay: '-3.5s', duration: '18s', left: '93%', size: '28px', animation: 'float-down', glow: 'engagement-blue-glow' },
          { icon: '💖', delay: '-9.5s', duration: '16s', left: '5%', size: '26px', animation: 'float-diagonal', glow: 'engagement-glow' },
          { icon: '🌟', delay: '-6.5s', duration: '17s', left: '95%', size: '24px', animation: 'float-zigzag', glow: 'engagement-blue-glow' },
          { icon: '📊', delay: '-11.5s', duration: '18s', left: '12%', size: '22px', animation: 'float-down', glow: '' },
          { icon: '🎊', delay: '-2.5s', duration: '15s', left: '85%', size: '26px', animation: 'float-diagonal', glow: 'engagement-glow' },
          { icon: '💬', delay: '-7.5s', duration: '19s', left: '50%', size: '24px', animation: 'float-zigzag', glow: '' },
          { icon: '🔔', delay: '-4.5s', duration: '16s', left: '70%', size: '28px', animation: 'float-down', glow: '' },
          { icon: '⚡', delay: '-10.5s', duration: '17s', left: '30%', size: '32px', animation: 'float-diagonal', glow: 'engagement-glow' },
          { icon: '🎁', delay: '-13.5s', duration: '18s', left: '60%', size: '26px', animation: 'float-zigzag', glow: '' }].
          map((item, i) =>
          <div
            key={i}
            className={`engagement-item ${item.glow}`}
            style={{
              left: item.left,
              animationName: item.animation,
              animationDelay: item.delay,
              animationDuration: item.duration,
              fontSize: item.size,
              opacity: 0.25
            }}>

              {item.icon}
            </div>
          )}
        </div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-20 lg:py-32">
          <div className="text-center space-y-4 sm:space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/10 border border-orange-500/30 rounded-full text-orange-300 text-sm font-medium">
              <Sparkles className="w-4 h-4" />
              <span>Método Comprovado para Crescer no Instagram</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black text-white leading-tight tracking-tight">
              Engajamento<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-orange-400 to-orange-500">
                Magnético
              </span>
            </h1>
            
            <p className="text-base sm:text-xl lg:text-2xl text-slate-300 max-w-3xl mx-auto leading-relaxed px-2">
              Guia prático para <strong className="text-white">crescer e vender</strong> no Instagram com estratégias aplicáveis a <strong className="text-white">qualquer nicho</strong>
            </p>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center pt-4 sm:pt-6">
              <a
                href="https://pay.kiwify.com.br/conkdhF"
                target="_blank"
                rel="noopener noreferrer"
                className="group w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-orange-600 to-orange-500 text-white text-base sm:text-lg font-bold rounded-full hover:shadow-2xl hover:shadow-orange-500/50 transition-all duration-300 flex items-center justify-center gap-2 hover:scale-105">

                Quero Crescer Agora
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="#conteudo"
                className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-white/5 backdrop-blur border border-white/20 text-white text-base sm:text-lg font-semibold rounded-full hover:bg-white/10 hover:border-orange-400/50 transition-all duration-300">

                Ver Conteúdo Completo
              </a>
            </div>

            <div className="flex flex-wrap justify-center gap-4 sm:gap-8 pt-8 sm:pt-12 text-xs sm:text-sm">
              <div className="flex items-center gap-2 text-slate-300">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span>28 páginas de conteúdo prático</span>
              </div>
              <div className="flex items-center gap-2 text-slate-300">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span>Calendário de 31 dias pronto</span>
              </div>
              <div className="flex items-center gap-2 text-slate-300">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span>Exercícios semanais</span>
              </div>
            </div>

            {/* Botão de Compra Adicional */}
            <div className="pt-6 sm:pt-8">
              <a
                href="https://pay.kiwify.com.br/conkdhF"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-blue-600 to-blue-500 text-white text-base sm:text-lg font-bold rounded-full hover:shadow-2xl hover:shadow-blue-500/50 transition-all duration-300 hover:scale-105 border border-blue-400/30">
                <Sparkles className="w-5 h-5" />
                Garantir Meu Acesso
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Problema Section */}
      <section className="py-12 sm:py-20 bg-slate-950/80 backdrop-blur" data-reveal="0">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center space-y-4 sm:space-y-6 mb-8 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold text-white">
              Você Está Postando, Mas...
            </h2>
            <p className="text-base sm:text-xl text-slate-300 max-w-3xl mx-auto px-2">
              Seu conteúdo não alcança ninguém? Você passa horas criando e não consegue vender?
            </p>
          </div>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
            <style>{`
              @keyframes fadeInUp {
                from { opacity: 0; transform: translateY(40px); }
                to { opacity: 1; transform: translateY(0); }
              }
              .reveal-active { animation: fadeInUp 0.8s cubic-bezier(0.22, 1, 0.36, 1) forwards; }
              .reveal-active:nth-child(1) { animation-delay: 0.1s; }
              .reveal-active:nth-child(2) { animation-delay: 0.2s; }
              .reveal-active:nth-child(3) { animation-delay: 0.3s; }
            `}</style>
            {[
            {
              icon: TrendingUp,
              title: "Baixo Alcance",
              desc: "Seus posts não aparecem para ninguém novo, só para os mesmos seguidores"
            },
            {
              icon: Users,
              title: "Sem Engajamento",
              desc: "Poucas curtidas, zero comentários e nenhum salvamento nos seus posts"
            },
            {
              icon: Target,
              title: "Não Vende",
              desc: "Você tem seguidores mas ninguém compra ou entra em contato"
            }].
            map((item, i) =>
            <div key={i} className={`p-4 sm:p-6 bg-white/5 backdrop-blur border border-white/10 rounded-2xl hover:border-orange-400/50 hover:bg-white/10 hover:scale-105 hover:shadow-2xl hover:shadow-orange-500/20 transition-all duration-300 opacity-0 ${visibleSections.has(0) ? 'reveal-active' : ''}`}>
                <item.icon className="w-10 h-10 sm:w-12 sm:h-12 text-orange-500 mb-3 sm:mb-4" />
                <h3 className="text-lg sm:text-xl font-bold text-white mb-2">{item.title}</h3>
                <p className="text-sm sm:text-base text-slate-400">{item.desc}</p>
              </div>
            )}
          </div>

          <div className="mt-8 sm:mt-12 p-6 sm:p-8 bg-gradient-to-r from-blue-900/30 to-orange-900/30 border border-orange-500/30 rounded-2xl text-center">
            <p className="text-lg sm:text-2xl text-white font-semibold">
              O problema não é você. É que <span className="text-orange-300">ninguém te ensinou o método certo.</span>
            </p>
          </div>
        </div>
      </section>

      {/* O que é Section */}
      <section className="py-12 sm:py-20" data-reveal="1">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-8 sm:gap-12 items-center">
            <div className={`transition-all duration-1000 ${visibleSections.has(1) ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
              <div className="inline-block px-4 py-2 bg-orange-500/10 border border-orange-500/30 rounded-full text-orange-300 text-sm font-medium mb-6">
                O que você vai aprender
              </div>
              <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6">
                18 Seções Práticas que Transformam seu Instagram
              </h2>
              <p className="text-sm sm:text-lg text-slate-300 mb-6 sm:mb-8 leading-relaxed">
                <strong className="text-white">Engajamento Magnético</strong> é o guia completo com tudo que você precisa para crescer organicamente, engajar de verdade e vender no Instagram — sem mitos, sem "truques mágicos", só estratégia comprovada.
              </p>
              <div className="space-y-3 sm:space-y-4">
                {[
                "Método testado em múltiplos nichos (Achadinhos, Moda, Marketing, Influenciador, Estética Automotiva)",
                "Aplicável do zero ou para quem já tem perfil e quer crescer",
                "Linguagem direta, sem enrolação — você entende e aplica no mesmo dia"].
                map((item, i) =>
                <div key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-300">{item}</span>
                  </div>
                )}
              </div>
            </div>
            <div className={`relative space-y-4 sm:space-y-6 transition-all duration-1000 delay-300 ${visibleSections.has(1) ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
              <div className="aspect-[4/3] bg-gradient-to-br from-blue-600/15 via-slate-900/40 to-orange-600/15 rounded-2xl sm:rounded-3xl border border-white/20 backdrop-blur-xl shadow-2xl shadow-blue-900/20 flex items-center justify-center p-4 sm:p-8 relative overflow-hidden">
                <style>{`
                  @keyframes growBar {
                    0%, 100% { height: 35%; }
                    50% { height: 100%; }
                  }
                  @keyframes countUp {
                    0% { opacity: 0; transform: translateY(20px) scale(0.8); }
                    100% { opacity: 1; transform: translateY(0) scale(1); }
                  }

                  @keyframes shimmer {
                    0%, 100% { opacity: 0.08; transform: translateX(-100%); }
                    50% { opacity: 0.15; transform: translateX(100%); }
                  }
                  @keyframes gridPulse {
                    0%, 100% { opacity: 0.03; }
                    50% { opacity: 0.08; }
                  }
                  @keyframes badgePulse {
                    0%, 100% { transform: scale(1); }
                    50% { transform: scale(1.05); }
                  }
                  .bar-1 { animation: growBar 3.5s ease-in-out infinite; box-shadow: 0 8px 32px rgba(37, 99, 235, 0.3); }
                  .bar-2 { animation: growBar 3.5s ease-in-out infinite 0.4s; box-shadow: 0 8px 32px rgba(59, 130, 246, 0.3); }
                  .bar-3 { animation: growBar 3.5s ease-in-out infinite 0.8s; box-shadow: 0 8px 32px rgba(251, 146, 60, 0.3); }
                  .bar-4 { animation: growBar 3.5s ease-in-out infinite 1.2s; box-shadow: 0 8px 32px rgba(251, 146, 60, 0.4); }
                  .bar-5 { animation: growBar 3.5s ease-in-out infinite 1.6s; box-shadow: 0 8px 32px rgba(234, 88, 12, 0.5); }
                  .num-1 { animation: countUp 1.2s cubic-bezier(0.34, 1.56, 0.64, 1); }
                  .num-2 { animation: countUp 1.2s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s backwards; }
                  .num-3 { animation: countUp 1.2s cubic-bezier(0.34, 1.56, 0.64, 1) 0.4s backwards; }
                  .num-4 { animation: countUp 1.2s cubic-bezier(0.34, 1.56, 0.64, 1) 0.6s backwards; }
                  .num-5 { animation: countUp 1.2s cubic-bezier(0.34, 1.56, 0.64, 1) 0.8s backwards; }

                  .shimmer-effect { animation: shimmer 6s ease-in-out infinite; }
                  .grid-bg { animation: gridPulse 5s ease-in-out infinite; }
                  .badge-pulse { animation: badgePulse 3s ease-in-out infinite; }
                `}</style>
                
                {/* Grid de Fundo Sutil */}
                <div className="absolute inset-0 grid-bg pointer-events-none" style={{
                  backgroundImage: 'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)',
                  backgroundSize: '40px 40px'
                }} />
                
                {/* Gráfico de Crescimento Refinado */}
                <div className="relative w-full h-full flex flex-col items-center justify-center z-10">
                  
                  {/* Linha Base */}
                  <div className="absolute bottom-32 left-1/2 -translate-x-1/2 w-64 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                  
                  {/* Barras do Gráfico com Sombra */}
                  <div className="flex items-end gap-3 sm:gap-6 mb-4 sm:mb-8 h-32 sm:h-52 relative">
                    <div className="bar-1 w-8 sm:w-12 bg-gradient-to-t from-blue-800 via-blue-600 to-blue-500 rounded-t-lg sm:rounded-t-xl relative">
                      <div className="absolute inset-0 bg-gradient-to-t from-white/20 to-transparent rounded-t-xl" />
                    </div>
                    <div className="bar-2 w-8 sm:w-12 bg-gradient-to-t from-blue-700 via-blue-500 to-blue-400 rounded-t-lg sm:rounded-t-xl relative">
                      <div className="absolute inset-0 bg-gradient-to-t from-white/20 to-transparent rounded-t-xl" />
                    </div>
                    <div className="bar-3 w-8 sm:w-12 bg-gradient-to-t from-blue-600 via-blue-500 to-orange-400 rounded-t-lg sm:rounded-t-xl relative">
                      <div className="absolute inset-0 bg-gradient-to-t from-white/20 to-transparent rounded-t-xl" />
                    </div>
                    <div className="bar-4 w-8 sm:w-12 bg-gradient-to-t from-orange-600 via-orange-500 to-orange-400 rounded-t-lg sm:rounded-t-xl relative">
                      <div className="absolute inset-0 bg-gradient-to-t from-white/25 to-transparent rounded-t-xl" />
                    </div>
                    <div className="bar-5 w-8 sm:w-12 bg-gradient-to-t from-orange-700 via-orange-500 to-orange-400 rounded-t-lg sm:rounded-t-xl relative">
                      <div className="absolute inset-0 bg-gradient-to-t from-white/30 to-transparent rounded-t-xl" />
                    </div>
                  </div>

                  {/* Números com Sombra de Texto */}
                  <div className="flex gap-3 sm:gap-8 relative">
                    <span className="num-1 text-sm sm:text-xl font-extrabold text-blue-300" style={{ textShadow: '0 2px 20px rgba(59, 130, 246, 0.5)' }}>1K</span>
                    <span className="num-2 text-base sm:text-2xl font-extrabold text-blue-400" style={{ textShadow: '0 2px 24px rgba(59, 130, 246, 0.6)' }}>10K</span>
                    <span className="num-3 text-lg sm:text-3xl font-extrabold text-blue-400" style={{ textShadow: '0 2px 28px rgba(59, 130, 246, 0.7)' }}>100K</span>
                    <span className="num-4 text-xl sm:text-4xl font-extrabold text-orange-400" style={{ textShadow: '0 3px 30px rgba(251, 146, 60, 0.8)' }}>500K</span>
                    <span className="num-5 text-2xl sm:text-5xl font-extrabold text-orange-400" style={{ textShadow: '0 4px 32px rgba(251, 146, 60, 0.9)' }}>1M</span>
                  </div>

                  {/* Efeito Shimmer */}
                  <div className="absolute inset-0 shimmer-effect bg-gradient-to-r from-transparent via-white/10 to-transparent pointer-events-none" />
                </div>
                
                {/* Borda Interna Brilhante */}
                <div className="absolute inset-0 rounded-3xl ring-1 ring-inset ring-white/10 pointer-events-none" />
              </div>
              
              {/* Value Badges */}
              <div className="grid grid-cols-3 gap-2 sm:gap-3">
                <div className="badge-pulse p-2 sm:p-4 bg-gradient-to-br from-blue-600/20 to-blue-800/20 border border-blue-400/30 rounded-lg sm:rounded-xl text-center">
                  <div className="text-lg sm:text-2xl font-black text-blue-300">+100</div>
                  <div className="text-[10px] sm:text-xs text-slate-300 font-medium">Nichos Testados</div>
                </div>
                <div className="badge-pulse p-2 sm:p-4 bg-gradient-to-br from-orange-600/20 to-orange-800/20 border border-orange-400/30 rounded-lg sm:rounded-xl text-center" style={{ animationDelay: '0.5s' }}>
                  <div className="text-lg sm:text-2xl font-black text-orange-300">31</div>
                  <div className="text-[10px] sm:text-xs text-slate-300 font-medium">Dias de Plano pronto</div>
                </div>
                <div className="badge-pulse p-2 sm:p-4 bg-gradient-to-br from-green-600/20 to-green-800/20 border border-green-400/30 rounded-lg sm:rounded-xl text-center" style={{ animationDelay: '1s' }}>
                  <div className="text-lg sm:text-2xl font-black text-green-300">7d</div>
                  <div className="text-[10px] sm:text-xs text-slate-300 font-medium">Garantia Total</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Conteúdo Section */}
      <section id="conteudo" className="py-20 bg-slate-950/80 backdrop-blur" data-reveal="2">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white">
              O que tem dentro?
            </h2>
            <p className="text-base sm:text-xl text-slate-300 px-2">
              18 seções práticas + calendário de 31 dias + exercícios semanais
            </p>
          </div>

          <div className="grid gap-4 sm:gap-6">
            {[
            { num: "01", title: "Otimize seu Perfil", desc: "Deixar o perfil claro em 5 segundos e converter visitas em seguidores" },
            { num: "02", title: "Criação de Conteúdo de Valor", desc: "Criar posts que geram salvamentos e compartilhamentos (o que o algoritmo mais recompensa)" },
            { num: "03", title: "Hashtags Estratégicas", desc: "Usar hashtags como contexto (sem mito) para aumentar alcance" },
            { num: "04", title: "Engajamento Consistente", desc: "Criar rotina de engajamento sem virar refém do celular" },
            { num: "05", title: "Colaborações e Parcerias", desc: "Crescer mais rápido com parcerias estratégicas" },
            { num: "06", title: "Regularidade e Horários", desc: "Consistência com frequência realista (3-5 posts/semana)" },
            { num: "07", title: "Concursos e Desafios", desc: "Ativar comunidade com desafios que funcionam" },
            { num: "08", title: "SEO do Instagram", desc: "Aparecer na busca com palavras-chave certas" },
            { num: "09", title: "UGC (Conteúdo Gerado por Usuários)", desc: "Transformar seguidores em prova social" },
            { num: "10", title: "Destaques (Story Highlights)", desc: "Organizar destaques para converter visitantes" },
            { num: "11", title: "Como o Instagram Entrega", desc: "Entender entrega por formato (Reels, Feed, Stories)" },
            { num: "12", title: "Métricas e Insights", desc: "Medir o que importa e ajustar estratégia" },
            { num: "13", title: "Pilares + Calendário Base", desc: "Estruturar pilares e calendário de 30 dias" },
            { num: "14", title: "Exercícios Semanais", desc: "4 semanas de desafios práticos para executar" },
            { num: "15", title: "Copy de Legenda", desc: "Escrever legendas que geram salvar, comentar e vender" },
            { num: "16", title: "Roteiro de Reels", desc: "Gravar Reels com roteiro simples que prende atenção" },
            { num: "17", title: "Funil do Instagram", desc: "Montar um funil completo: alcance → confiança → venda" },
            { num: "18", title: "Calendário Detalhado (31 dias)", desc: "Calendário pronto com tema, hook e CTA para cada dia" }].
            map((item, i) =>
            <div key={i} className={`group p-4 sm:p-6 bg-white/5 backdrop-blur border border-white/10 rounded-xl sm:rounded-2xl hover:border-orange-400/50 hover:bg-white/10 hover:scale-[1.02] hover:shadow-xl hover:shadow-blue-500/10 transition-all duration-300 opacity-0 ${visibleSections.has(2) ? 'reveal-active' : ''}`} style={{ animationDelay: `${i * 0.05}s` }}>
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-600 to-orange-600 rounded-lg sm:rounded-xl flex items-center justify-center font-bold text-sm sm:text-base text-white">
                    {item.num}
                  </div>
                  <div>
                    <h3 className="text-base sm:text-lg font-bold text-white mb-1 sm:mb-2 group-hover:text-orange-300 transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-slate-400 text-xs sm:text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Bônus Section */}
      <section className="py-20" data-reveal="3">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-white">
              Bônus Inclusos
            </h2>
            <p className="text-xl text-slate-300">
              Material extra para você aplicar imediatamente
            </p>
          </div>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
            {[
            {
              title: "Calendário de 31 Dias Pronto",
              desc: "Tema, Hook e CTA para cada dia do mês. É só copiar e adaptar para o seu nicho.",
              badge: "Valor: R$ 27"
            },
            {
              title: "Modelos de Copy Prontos",
              desc: "CTAs, hooks e legendas testadas para salvar, comentar e vender.",
              badge: "Valor: R$ 27"
            },
            {
              title: "4 Semanas de Exercícios",
              desc: "Desafios semanais práticos para você executar e ver resultados rápidos.",
              badge: "Valor: R$ 27"
            }].
            map((item, i) =>
            <div key={i} className={`relative p-6 sm:p-8 bg-gradient-to-br from-blue-900/30 to-orange-900/30 border-2 border-orange-500/40 rounded-xl sm:rounded-2xl hover:border-orange-400/60 hover:scale-105 hover:shadow-2xl hover:shadow-orange-500/30 transition-all duration-300 opacity-0 ${visibleSections.has(3) ? 'reveal-active' : ''}`} style={{ animationDelay: `${i * 0.2}s` }}>
                <div className="absolute -top-3 left-4 sm:left-6 px-3 sm:px-4 py-1 bg-gradient-to-r from-orange-600 to-orange-500 text-white text-xs sm:text-sm font-bold rounded-full">
                  {item.badge}
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-white mb-2 sm:mb-3 mt-2">{item.title}</h3>
                <p className="text-sm sm:text-base text-slate-300">{item.desc}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Para Quem é Section */}
      <section className="py-20 bg-slate-950/80 backdrop-blur" data-reveal="4">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white text-center mb-8 sm:mb-16">
            Para Quem é Este Ebook?
          </h2>

          <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
            <div className={`p-6 sm:p-8 bg-green-900/20 border-2 border-green-500/30 rounded-xl sm:rounded-2xl transition-all duration-1000 ${visibleSections.has(4) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <h3 className="text-xl sm:text-2xl font-bold text-green-400 mb-4 sm:mb-6">✓ Este ebook é para você se...</h3>
              <ul className="space-y-3 sm:space-y-4">
                {[
                "Você posta mas não consegue alcance nem engajamento",
                "Quer crescer organicamente sem depender de anúncios",
                "Precisa de um método claro e aplicável no mesmo dia",
                "Quer transformar seguidores em clientes",
                "Trabalha com qualquer nicho (Achadinhos, Moda, Marketing, Serviços, Produtos)"].
                map((item, i) =>
                <li key={i} className="flex items-start gap-2 sm:gap-3 text-sm sm:text-base text-slate-200">
                    <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                )}
              </ul>
            </div>

            <div className={`p-6 sm:p-8 bg-red-900/20 border-2 border-red-500/30 rounded-xl sm:rounded-2xl transition-all duration-1000 delay-300 ${visibleSections.has(4) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <h3 className="text-xl sm:text-2xl font-bold text-red-400 mb-4 sm:mb-6">✗ Não é para você se...</h3>
              <ul className="space-y-4">
                {[
                "Você quer crescimento instantâneo sem esforço",
                "Procura 'truques mágicos' ou 'hacks' milagrosos",
                "Não está disposto a aplicar o método consistentemente",
                "Acha que só anúncio resolve tudo",
                "Não quer aprender e prefere copiar sem estratégia"].
                map((item, i) =>
                <li key={i} className="flex items-start gap-3 text-slate-200">
                    <div className="w-6 h-6 flex-shrink-0 mt-0.5 text-red-400 text-xl">✗</div>
                    <span>{item}</span>
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final Section */}
      <section id="comprar" className="py-12 sm:py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/50 to-orange-900/50"></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="space-y-6 sm:space-y-8">
            <h2 className="text-3xl sm:text-4xl lg:text-6xl font-black text-white tracking-tight">
              Comece a Crescer<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-orange-400 to-orange-500">
                Hoje Mesmo
              </span>
            </h2>
            
            <div className="p-6 sm:p-8 bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl sm:rounded-3xl">
              <div className="space-y-4 sm:space-y-6">
                <div>
                  <div className="text-slate-400 text-base sm:text-lg mb-2">De <span className="line-through">R$ 67,00</span></div>
                  <div className="text-4xl sm:text-6xl font-black text-white">R$ 29,90</div>
                  <div className="text-orange-300 text-base sm:text-xl mt-2">Investimento único • Acesso imediato</div>
                </div>

                <a
                  href="https://pay.kiwify.com.br/conkdhF"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 sm:gap-3 px-8 sm:px-10 py-4 sm:py-5 bg-gradient-to-r from-orange-600 to-orange-500 text-white text-lg sm:text-xl font-bold rounded-full hover:shadow-2xl hover:shadow-orange-500/50 transition-all duration-300 hover:scale-105">

                  Sim, Quero Crescer Agora
                  <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
                </a>

                <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 text-xs sm:text-sm text-slate-300 pt-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    <span>Acesso imediato por email</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    <span>PDF completo (28 páginas)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    <span>Bônus inclusos</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-6 sm:pt-8">
              <p className="text-slate-300 text-sm sm:text-lg">
                🔒 Compra 100% segura • Pagamento processado por plataforma confiável
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Garantia Section */}
      <section className="py-20 bg-slate-950/80 backdrop-blur">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="p-6 sm:p-10 bg-gradient-to-br from-green-900/30 to-emerald-900/30 border-2 border-green-500/30 rounded-2xl sm:rounded-3xl text-center">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
              <CheckCircle className="w-10 h-10 sm:w-12 sm:h-12 text-white" />
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-3 sm:mb-4">Garantia de 7 Dias</h3>
            <p className="text-sm sm:text-lg text-slate-200 leading-relaxed">
              Se você aplicar o método e não ver <strong className="text-white">nenhum resultado</strong> nos próximos 7 dias, devolvemos <strong className="text-white">100% do seu dinheiro</strong>. Simples assim.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20" data-reveal="5">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl sm:text-5xl font-bold text-white text-center mb-16">
            Perguntas Frequentes
          </h2>

          <div className="space-y-4 sm:space-y-6">
            {[
            {
              q: "O método funciona para o meu nicho?",
              a: "Sim! O ebook foi criado com exemplos em 5 nichos diferentes (Achadinhos, Moda, Marketing, Influenciador, Estética Automotiva) justamente para mostrar que a estratégia funciona em qualquer área. Você só precisa adaptar os exemplos para o seu nicho."
            },
            {
              q: "Eu preciso de muitos seguidores para começar?",
              a: "Não! O método funciona do zero ou para quem já tem perfil. Na verdade, quanto antes você aplicar as estratégias corretas, mais rápido vai crescer de forma orgânica e sustentável."
            },
            {
              q: "Quanto tempo leva para ver resultados?",
              a: "Isso depende da sua consistência. O ebook inclui desafios semanais e um calendário de 31 dias pronto. Muitas pessoas começam a ver melhora no alcance e engajamento já na primeira semana aplicando o método."
            },
            {
              q: "Preciso pagar mensalidade?",
              a: "Não! É um pagamento único de R$ 29,90 e você tem acesso vitalício ao material completo em PDF."
            },
            {
              q: "Como vou receber o ebook?",
              a: "Assim que o pagamento for confirmado, você recebe o PDF completo direto no seu email. Acesso imediato."
            },
            {
              q: "O conteúdo é atualizado?",
              a: "O ebook ensina fundamentos e estratégias que funcionam independente das mudanças do Instagram. O foco é no comportamento do algoritmo (retenção, salvamentos, compartilhamentos) que não muda."
            }].
            map((item, i) =>
            <div key={i} className={`p-4 sm:p-6 bg-white/5 backdrop-blur border border-white/10 rounded-xl sm:rounded-2xl hover:border-orange-400/50 hover:bg-white/10 hover:scale-[1.02] transition-all duration-300 opacity-0 ${visibleSections.has(5) ? 'reveal-active' : ''}`} style={{ animationDelay: `${i * 0.1}s` }}>
                <h3 className="text-base sm:text-xl font-bold text-white mb-2 sm:mb-3">{item.q}</h3>
                <p className="text-sm sm:text-base text-slate-300 leading-relaxed">{item.a}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 sm:py-12 bg-black border-t border-white/10">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center space-y-3 sm:space-y-4">
            <div className="flex items-center justify-center gap-2 text-white text-lg sm:text-xl font-bold">
              <img src="https://static.lumi.new/material/ac/acdac8331d4533e6843bbea3ae5d2169.webp" alt="Logo" className="w-6 h-6" />
              <span>Engajamento Magnético</span>
            </div>
            <p className="text-sm sm:text-base text-slate-400 px-4">
              Criado por <strong className="text-white">Muita Mídia LTDA</strong> • <a href="https://www.muitamidiaofc.com.br" target="_blank" rel="noopener noreferrer" className="text-orange-400 hover:text-orange-300 transition-colors underline">www.muitamidiaofc.com.br</a>
            </p>
            <p className="text-slate-500 text-sm">
              © 2024 Todos os direitos reservados
            </p>
          </div>
        </div>
      </footer>
    </div>);

}

export default App;